// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import appActions from 'common/support/applicationActions';
import focusManager from 'support/focus/FocusManager';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration from 'components/withRestoration';

import { getAppScalingFactor } from 'support/scalingHelper';

import styles from './Grid.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

class Grid extends React.Component {
  gridViewport = React.createRef();

  firstRow = React.createRef();

  focusGroup = React.createRef();

  focusedIndex = 0;

  timeout = null;

  rowHeight = null;

  focusId = null;

  constructor(props) {
    const { restorationState } = props;

    super(props);

    this.focusId = props.focusId || focusManager.getNewUniqueId();

    if (restorationState) {
      this.state = restorationState;
    } else {
      this.state = {
        isAnimating: false,
        top: 0,
        endIndex: this.getEndIndex(0),
        startIndex: this.getStartIndex(0),
      };
    }
  }

  componentDidMount() {
    const { data } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && data.length) {
      this.rowHeight = this.firstRow.current.getBoundingClientRect().height;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, setRestorationState } = this.props;

    if (!prevProps.data && data && data.length) {
      this.rowHeight = this.firstRow.current.getBoundingClientRect().height;
    }

    if (prevState !== this.state) {
      setRestorationState(this.state);
    }
  }

  componentWillUnmount() {
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = () => {
    if (!window || !focusManager.isFocusAncestor(this.focusId)) return;

    const focusedEl = focusManager.getFocusableElement(
      focusManager.currentFocusId,
    );

    const {
      top: rectTop,
      bottom: rectBottom,
    } = focusedEl.getBoundingClientRect();
    const {
      top: viewportTop,
      bottom: viewportBottom,
    } = this.gridViewport.current.getBoundingClientRect();

    const offsetTop = focusedEl.offsetTop * scaling;
    const viewportHeight = viewportBottom - viewportTop;
    const itemHeight = rectBottom - rectTop;

    if (rectTop < viewportTop) {
      this.setState(
        {
          isAnimating: true,
          top: Math.min(0, -offsetTop + 50 * scaling),
        },
        () => {
          if (!this.timeout) {
            this.timeout = setTimeout(() => {
              this.timeout = null;
              this.handleClipping();
            }, 300);
          }
        },
      );
    } else if (rectBottom > viewportBottom) {
      this.setState(
        {
          isAnimating: true,
          top: -offsetTop + viewportHeight - itemHeight - 50 * scaling,
        },
        () => {
          if (!this.timeout) {
            this.timeout = setTimeout(() => {
              this.timeout = null;
              this.handleClipping();
            }, 300);
          }
        },
      );
    } else {
      this.handleClipping();
    }
  };

  getStartIndex(focusedIndex) {
    const { prevBuffer, numCols } = this.props;
    return (
      Math.floor(Math.max(0, focusedIndex - prevBuffer) / numCols) * numCols
    );
  }

  getEndIndex(focusedIndex) {
    const { nextBuffer, prevBuffer, numCols } = this.props;

    return (
      this.getStartIndex(focusedIndex) +
      Math.ceil((nextBuffer + prevBuffer) / numCols) * numCols
    );
  }

  onFocusIndexOverride = (action, currentIndex) => {
    const { numCols, data } = this.props;
    const { startIndex } = this.state;

    let newIndex = false;
    switch (action) {
      case appActions.moveUp:
        newIndex = currentIndex - numCols;
        break;
      case appActions.moveDown:
        if (currentIndex + numCols <= data.length - 1 - startIndex) {
          newIndex = currentIndex + numCols;
          break;
        }

        // Only want to push the index to the upper bound if the upper
        // bound is on the next row.  Otherwise, we don't want to handle
        // the action
        newIndex =
          Math.floor((data.length - 1 - startIndex) / numCols) >
          Math.floor(currentIndex / numCols)
            ? data.length - 1 - startIndex
            : false;
        break;
      case appActions.moveLeft:
        // if first item in row then return currentIndex
        if (currentIndex % numCols === 0) {
          newIndex = currentIndex;
          focusManager.setCurrentFocus('left-nav-focusable-content');
        }
        break;
      case appActions.moveRight:
        // if last item in row then return currentIndex
        if (currentIndex % numCols === numCols - 1) {
          newIndex = currentIndex;
        }
        break;
      default:
        newIndex = false;
    }

    if (action !== appActions.moveUp && action !== appActions.moveDown) {
      return newIndex;
    }

    const {
      top: viewportTop,
      bottom: viewportBottom,
    } = this.gridViewport.current.getBoundingClientRect();

    const gridFocusChildren = focusManager.getFocusableChildren(this.focusId);
    const newChild = gridFocusChildren[newIndex];

    if (newChild) {
      const {
        top: newFocusTop,
        bottom: newFocusBottom,
      } = newChild.getBoundingClientRect();

      if (
        this.timeout &&
        (newFocusTop < viewportTop || newFocusBottom > viewportBottom)
      ) {
        return 'handled';
      }
    }

    return newIndex;
  };

  onFocusIndexChanged = index => {
    this.focusedIndex = index;
  };

  splitData = arr => {
    const { numCols } = this.props;
    return arr.reduce((accumulator, value, index) => {
      const accumulatorIndex = Math.floor(index / numCols);
      if (!accumulator[accumulatorIndex]) {
        accumulator.push([]);
      }
      accumulator[accumulatorIndex].push(value);
      return accumulator;
    }, []);
  };

  handleClipping = () => {
    const { numCols } = this.props;
    const { startIndex, endIndex, top } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = this.focusedIndex + startIndex;
    const newStartIndex = this.getStartIndex(focusedIndex);
    const newEndIndex = this.getEndIndex(focusedIndex);

    if (
      (newStartIndex !== startIndex || newEndIndex !== endIndex) &&
      this.rowHeight
    ) {
      const amountToClip = Math.abs(newStartIndex - startIndex) / numCols;
      this.setState(
        {
          isAnimating: false,
          startIndex: newStartIndex,
          endIndex: newEndIndex,
          top:
            newStartIndex > startIndex
              ? top + this.rowHeight * amountToClip
              : top - this.rowHeight * amountToClip,
        },
        () => {
          if (this.focusGroup && this.focusGroup.current) {
            this.focusGroup.current.resetFocusIndex();
          }
        },
      );
    }
  };

  render() {
    const { numCols, renderItem, data, className } = this.props;
    const { top, startIndex, endIndex, isAnimating } = this.state;

    const splitData = this.splitData(data);
    return (
      <div className={className} ref={this.gridViewport}>
        <FocusGroup
          className={classNames(
            'flex-wrap',
            'h-100',
            'w-100',
            'align-content-start',
            'justify-content-center',
            {
              [styles.animate]: isAnimating,
            },
            styles.grid,
          )}
          focusId={this.focusId && this.focusId.toString()}
          onFocusIndexOverride={this.onFocusIndexOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          ref={this.focusGroup}
          style={{
            transform: `translateY(${top / scaling}px)`,
            WebkitTransform: `translateY(${top / scaling}px)`,
          }}
          horizontal
        >
          {splitData
            .slice(startIndex / numCols, endIndex / numCols + 1)
            .map((row, rowIndex) => (
              <div
                className={classNames(
                  'grid-row d-flex justify-content-start w-100',
                  styles.gridrow,
                )}
                ref={rowIndex === 0 ? this.firstRow : undefined}
                key={row[0].id}
              >
                {row.map((item, index) =>
                  renderItem(item, index + rowIndex * numCols + startIndex),
                )}
              </div>
            ))}
        </FocusGroup>
      </div>
    );
  }
}

Grid.propTypes = {
  numCols: PropTypes.number.isRequired,
  renderItem: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    (propValue, key, componentName, location, propFullName) => {
      if (propValue[key].id || propValue[key].id === 0) {
        return null;
      }

      return new Error(
        `Invalid prop ${propFullName} supplied to ${componentName}.  Data items must contain an id.`,
      );
    },
  ).isRequired,
  prevBuffer: PropTypes.number.isRequired,
  nextBuffer: PropTypes.number.isRequired,
  className: PropTypes.string,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restorationState: PropTypes.shape({
    isAnimating: PropTypes.bool.isRequired,
    top: PropTypes.number.isRequired,
    endIndex: PropTypes.number.isRequired,
    startIndex: PropTypes.number.isRequired,
  }),
  setRestorationState: PropTypes.func.isRequired,
};

Grid.defaultProps = {
  className: null,
  focusId: null,
  restorationState: null,
};

export default withRestoration(props => props.restorationId)(Grid);
